import { ChangeEvent, CSSProperties, useEffect, useRef, useState } from "react";
import { Button, styled, SxProps, Theme } from "@mui/material";
import Cropper from "react-easy-crop";
import { ActionBtn } from "../../../components/Typography";
import { Section } from "../index";
import { Area, Point } from "react-easy-crop/types";
import getCroppedImg from "../cropImage";
import { sizing } from "../../../utils/methods";
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';

type ContentUploadProps = {
  onUpload?(): Promise<any>;
  setPreview?(value: any): void;
  setFile?(file: File | undefined): void;
  sx?: SxProps<Theme>;
  additionalSx?: SxProps<Theme>;
  cropperStyle?: CSSProperties;
  onlyImages?: boolean;
  profileImage?: boolean;
  title?: string;
  loading?: boolean;
};

const ContentUpload = ({
  sx,
  title,
  additionalSx,
  setPreview,
  setFile,
  onlyImages,
  cropperStyle,
  profileImage,
  onUpload,
  loading,
}: ContentUploadProps) => {
  const MAX_FILE_SIZE = 25000000;

  const uploadRef = useRef<HTMLInputElement>(null);
  const CroppingBlockRef = useRef<HTMLDivElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
  const [filePreview, setFilePreview] = useState<string | undefined>(undefined);

  const [error, setError] = useState("");

  const [cropping, setCropping] = useState<boolean>(false);

  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const [croppedImage, setCroppedImage] = useState<File | undefined>(undefined);
  const [croppedImagePreview, setCroppedImagePreview] = useState<string | undefined>(undefined);

  const onCropComplete = (_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels as any);
  };

  const showCroppedImage = async () => {
    try {
      const croppedImage = await getCroppedImg(filePreview, croppedAreaPixels);
      setCroppedImage(croppedImage as File);
      setCroppedImagePreview(URL.createObjectURL(croppedImage as Blob));
      setSelectedFile(croppedImage as File);
      if (setFile) {
        setFile(croppedImage as File);
      }
    } catch (e) {
      console.error(e);
    }
  };
  useEffect(() => {
    if (selectedFile?.type !== "image/gif" && filePreview && !cropping) {
      setCropping(true);
      window.scrollTo({ top: CroppingBlockRef.current?.offsetTop || 600, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setCropping(false);
    }
  }, [selectedFile, filePreview]);
  useEffect(() => {
    if (!filePreview) {
      setCroppedImage(undefined);
      setCroppedImagePreview(undefined);
    }
  }, [filePreview, croppedImage, croppedImagePreview]);

  useEffect(() => {
    if (!selectedFile) {
      setFilePreview(undefined);
      setCroppedImagePreview(undefined);
      if (setPreview) {
        setPreview(undefined);
      }
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setFilePreview(objectUrl);
    if (setPreview) {
      setPreview(objectUrl);
    }

    // free memory when ever this component is unmounted
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const handleButtonClick = () => {
    uploadRef.current?.click();
  };

  const handleClearClick = () => {
    if (uploadRef.current) {
      // @ts-ignore
      uploadRef.current.value = null;

      setSelectedFile(undefined);
      setCroppedImage(undefined);
      if (setFile) {
        setFile(undefined);
      }
    }
  };

  const handleProfileUpload = async () => {
    if (onUpload) {
      await onUpload();
    }
  };

  const handleUpload = (e: ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files?.length) {
      const file = e.target.files[0];

      if (file.size > MAX_FILE_SIZE) {
        setError(`Selected file is too big! Size should not exceed ${MAX_FILE_SIZE / 1000000} MB`);
      } else {
        if (onlyImages && file.type === "image/gif") {
          setError("GIF Files are not allowed for profile pictures");
        } else if (file?.type === "image/gif" && setFile) {
          const img = new Image();
          const reader = new FileReader();
          reader.onload = function (e) {
            console.log({ img });
            img.onload = (e) => {
              console.log(e);
              console.log(img);
              const width = img.width;
              const height = img.height;
              console.log(`Width: ${width}, Height: ${height}`);

              if (width < 200) {
                setError(`Selected GIF resolution is too small (${width}x${height})! Should be at least 200x200 px`);
              } else if (width !== height) {
                setError(`Selected GIF is not square! Width and height should be equal`);
              } else {
                setFile(file);
              }
            };
            img.src = e.target?.result as any;

            img.onerror = (e) => {
              console.log("onerror", { e });
            };

            img.onload = () => {
              const width = img.width;
              const height = img.height;
              console.log(`Width: ${width}, Height: ${height}`);

              if (width < 200) {
                setError(`Selected GIF resolution is too small (${width}x${height})! Should be at least 200x200 px`);
                // } else if (width !== height) {
                //   setError(`Selected GIF is not square! Width and height should be equal`);
              } else {
                setSelectedFile(file);
                setFile(file);
              }
            };
          };

          reader.readAsDataURL(file);
        } else {
          setSelectedFile(file);
          setError("");
        }
      }
    }
  };

  let accept = "image/png, image/gif, image/jpeg";
  if (onlyImages) {
    accept = "image/png, image/jpeg";
  }

  return (
    <Wrapper
      ref={CroppingBlockRef}
      sx={{
        p: 12,
        backgroundColor: ({ palette }) => palette.background.paper,
        borderColor: ({ palette }) => palette.action.disabled,
        ...sx,
      }}
      className={(error ? "error " : "") + (cropping ? "cropping" : "")}
    >
      <EmptyBlock sx={{ display: selectedFile || croppedImagePreview ? "none" : "flex", ...additionalSx }}>
        {title && <p>{title}</p>}
        {!title && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <ImageOutlinedIcon fontSize="large" />
            <p style={{ margin: 0 }}>
              Choose GIF file here to upload <b>(max 25MB)</b>
            </p>
          </div>

        )}
        {!selectedFile && (
          <Button sx={{ mt: 0 }} onClick={() => handleButtonClick()}>
            Choose File
          </Button>
        )}
      </EmptyBlock>
      <input accept={accept} ref={uploadRef} type="file" hidden onChange={handleUpload} />
      {!!croppedImagePreview && cropping && (
        <Preview className={profileImage ? "ProfileImagePreview" : ""} src={croppedImagePreview} alt={"NFT image"} />
      )}
      {!!filePreview && !cropping && <Preview src={filePreview} alt={"NFT image"} />}
      {!croppedImagePreview && cropping && (
        <Section sx={{ paddingTop: cropperStyle && sizing(0) }}>
          <Cropper
            style={{
              containerStyle: {
                position: "relative",
                height: "40vh",
                maxHeight: "400px",
                ...cropperStyle,
              },
            }}
            image={filePreview}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape={profileImage ? "round" : "rect"}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
          />
          <ActionBtn
            sx={{ margin: "0 auto", width: "200px", display: "flex", alignSelf: "center" }}
            onClick={() => showCroppedImage()}
          >
            Crop
          </ActionBtn>
        </Section>
      )}
      {onUpload && profileImage && croppedImage && (
        <Button disabled={loading} sx={{ margin: 0, width: "200px" }} onClick={() => handleProfileUpload()}>
          Upload
        </Button>
      )}
      {(croppedImage || selectedFile) && (
        <Button color="primary" sx={{ width: "200px" }} variant="outlined" onClick={() => handleClearClick()}>
          Clear
        </Button>
      )}
      {error && <Error>{error}</Error>}
    </Wrapper>
  );
};

const EmptyBlock = styled("div")`
  width: 100%;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 560px) {
    flex-direction: column;
    justify-content: center;
  }
`;

const Wrapper = styled("div")`
  border-radius: 16px;
  border: 2px dashed;
  margin: 0 auto;
  //min-height: 400px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  position: relative;

  &.error {
    border-color: ${({ theme }) => theme.palette.error.main};
  }

  p {
    margin: 0;
    color: #3d3d3d;
  }

  button {
    margin-top: 10px;
    padding: 6px 42px;
  }

  &.cropping {
    width: 100%;
    height: auto;
  }
`;

const Preview = styled("img")`
  display: block;
  width: 100%;
  max-width: 400px;
  height: auto;
  &.ProfileImagePreview {
    border-radius: 50%;
    max-width: 300px;
  }
  @media (max-width: 900px) {
    display: block;
  }
`;

const Error = styled("div")`
  margin-top: 24px;
  color: ${({ theme }) => theme.palette.error.main};
  font-size: 10px;
`;

export default ContentUpload;
